import { addDays, isAfter, isBefore } from 'date-fns';
import { type Program, type ProgramWithingsOrderPostContentObjectList, type Topic, getMinContentDate } from 'models';
import { useTranslation } from 'react-i18next';
import {
  DateInput,
  type DatePickerDateType,
  Form,
  FormActions,
  Input,
  Modal,
  RichTextInput,
  SelectInput,
  getDatePickerDateType,
  getDateType,
} from 'ui';
import { useProgramMentions } from '../../../hooks';
import { useGetWithingsOrderPostInitialValues } from './hooks';

export type WithingsOrderPostFormValues = {
  htmlContent: string;
  programTopicId: string;
  publishAt: DatePickerDateType;
  textContent: string;
  title: string;
  todoDueDate: DatePickerDateType;
};

export type WithingsOrderPostSubmitData = {
  htmlContent: string;
  programTopicId: string;
  publishAt: Date;
  textContent: string;
  title: string;
  todoDueDate: Date;
};

type P = {
  loading: boolean;
  objectData?: ProgramWithingsOrderPostContentObjectList;
  onRequestReturn?: () => void;
  onSubmit: (data: WithingsOrderPostSubmitData) => void;
  preSelectedProgramTopic?: Topic;
  program: Program;
  submitText?: string;
};

export const WithingsOrderPostForm = ({
  loading,
  objectData,
  onRequestReturn,
  onSubmit,
  preSelectedProgramTopic,
  program,
  submitText,
}: P) => {
  const [form] = Form.useForm<WithingsOrderPostFormValues>();
  const { t } = useTranslation();
  const initialValues = useGetWithingsOrderPostInitialValues({ objectData, preSelectedProgramTopic, program });
  const handleOnRequestMentions = useProgramMentions(program.id);

  const onFinish = (data: WithingsOrderPostFormValues) => {
    onSubmit({
      htmlContent: data.htmlContent,
      programTopicId: data.programTopicId,
      publishAt: getDateType(data.publishAt),
      textContent: data.textContent,
      title: data.title,
      todoDueDate: getDateType(data.todoDueDate),
    });
  };

  return (
    <Form<WithingsOrderPostFormValues> form={form} initialValues={initialValues} onFinish={onFinish}>
      <SelectInput<Topic>
        input={{
          getOptionLabel: (option) => option.name,
          getOptionValue: (option) => option.id,
          options: program.topics,
          placeholder: t('Select module'),
        }}
        item={{
          'data-test-id': 'select_program-topic',
          label: 'Program module',
          name: 'programTopicId',
          rules: [{ required: true }],
        }}
      />
      <DateInput
        input={{
          minimum: getMinContentDate({ startTime: program.startTime }),
          showTime: true,
        }}
        item={{
          getValueFromEvent(newPublishDate: DatePickerDateType): DatePickerDateType {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: in field values we need to asset type
            const currentDueDate = form.getFieldValue('todoDueDate') as DatePickerDateType | undefined;

            if (currentDueDate && isBefore(getDateType(currentDueDate), getDateType(newPublishDate))) {
              form.setFieldsValue({ todoDueDate: getDatePickerDateType(addDays(getDateType(newPublishDate), 1)) });
            }

            return newPublishDate;
          },
          label: 'Publish date',
          name: 'publishAt',
          rules: [
            { required: true },
            {
              message: t('Error: Post publish date must be set after Program start time'),
              // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
              async validator(_, value: DatePickerDateType) {
                if (isBefore(getDateType(value), program.startTime)) {
                  throw new Error(t('Error: Post publish date must be set after Program start time'));
                }
              },
            },
            {
              message: t('Error: Post publish date must be set before Program end time'),
              // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
              async validator(_, value: DatePickerDateType) {
                if (isAfter(getDateType(value), program.endTime)) {
                  throw new Error(t('Error: Post publish date must be set before Program end time'));
                }
              },
            },
          ],
        }}
      />
      <DateInput
        input={{
          minimum: getMinContentDate({ startTime: program.startTime }),
          showTime: true,
        }}
        item={{
          dependencies: ['publishAt'],
          label: 'To-Do due date',
          name: 'todoDueDate',
          rules: [
            { required: true },
            {
              message: t('Error: To-Do due date must be set after Program start time'),
              // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
              async validator(_, value: DatePickerDateType) {
                if (isBefore(getDateType(value), program.startTime)) {
                  throw new Error(t('Error: To-Do due date must be set after Program start time'));
                }
              },
            },
            {
              message: t('Error: To-Do due date must be set before Program end time'),
              // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
              async validator(_, value: DatePickerDateType) {
                if (isAfter(getDateType(value), program.endTime)) {
                  throw new Error(t('Error: To-Do due date must be set before Program end time'));
                }
              },
            },
            {
              message: t('Error: Due to date must be set after the publish date'),
              // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
              async validator(_, value: DatePickerDateType) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: in field values we need to asset type
                const publishAt = form.getFieldValue('publishAt') as DatePickerDateType | undefined;
                if (publishAt && isBefore(getDateType(value), getDateType(publishAt))) {
                  throw new Error(t('Error: Due to date must be set after the publish date'));
                }
              },
            },
          ],
        }}
      />
      <Form.Item<WithingsOrderPostFormValues> label="Title" name="title" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <RichTextInput
        html={{
          label: t('Content'),
          mentions: handleOnRequestMentions,
          name: 'htmlContent',
          rules: [{ required: true }],
        }}
        text={{ name: 'textContent' }}
      />
      <Modal.HugContent.Footer>
        <FormActions
          right={onRequestReturn ? { onClick: onRequestReturn } : undefined}
          submit={{ children: submitText, loading }}
        />
      </Modal.HugContent.Footer>
    </Form>
  );
};
